import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  HStack,
  Heading,
  Progress,
  VStack,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Spacer,
} from "@chakra-ui/react";
import AvatarVideo from "../assets/videos/Animated_avatar.mp4"; // Importing the avatar video
import axiosInstance from "../helpers/axiosInstance"; // Import your axios instance
import CookieConsent from "react-cookie-consent";
import { validateEmail } from "../utils/validation";
import Translate from "../components/LanguageTranslation/Translate";
import { useTranslation } from "../components/LanguageTranslation/TranslationContext";

const CookieConsentPopup = ({ onAccept }) => {
  const [showConfigure, setShowConfigure] = useState(false); // Toggle configuration options
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Necessary cookies are always enabled
    analytics: true,
    marketing: true,
  });

  // Handle changes in cookie preferences
  const handlePreferenceChange = (category) => {
    setCookiePreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const savePreferences = () => {
    // Save preferences to localStorage or cookie
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences)
    );
    onAccept(); // Mark cookies as accepted
  };

  return (
    <CookieConsent
      location="bottom"
      buttonText="Accept All"
      cookieName="nomad360CookieConsent"
      expires={150}
      overlay={false}
      ButtonComponent={() => null} // Hides default button
      style={{
        background: "none",
        color: "white",
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100vw",
        padding: 0,
        margin: 0,
        zIndex: 9999,
      }}
      containerClasses="cookie-bar-content"
    >
      <Box
        w={{ base: "100vw", md: "400px", lg: "400px", xl: "500px" }}
        p={{ base: 4, md: 4 }}
        bg="#292929"
        color="white"
        borderRadius={{ base: 0, md: "md" }}
        boxShadow="lg"
        zIndex="1000"
        position={{ base: "fixed", md: "relative" }}
        bottom={{ base: 0, md: "auto" }}
        left={{ base: 0, md: "auto" }}
      >
        <Text mb={2} fontSize={{ base: "sm", md: "md" }} color="#E1E6DB">
          <Translate text="Our Use of Cookies" />
        </Text>
        <Text fontSize={{ base: "xs", md: "sm" }} color="#E1E6DB" mb={4}>
          <Translate
            text="By clicking 'Accept Cookies', you consent to NOMAD360's use of cookies
          and other tracking technologies to enhance your browsing experience."
          />
        </Text>

        {!showConfigure ? (
          <VStack w="100%" align="center">
            <Button
              bg="#E1E6DB"
              color="#292929"
              border="1px solid black"
              size={{ base: "sm", md: "md" }}
              w="100%"
              onClick={() => savePreferences()}
            >
              <Translate text="Accept Cookies" />
            </Button>
            <Button
              variant="outline"
              bg="#292929"
              color="#E1E6DB"
              border="1px solid #E1E6DB"
              size={{ base: "sm", md: "md" }}
              _hover="none"
              onClick={() => setShowConfigure(true)} // Show configuration options
              w="100%"
            >
              <Translate text="Manage Cookies" />
            </Button>
          </VStack>
        ) : (
          <VStack align="start" spacing={3} mt={4}>
            <Checkbox
              isChecked={cookiePreferences.necessary}
              isDisabled
              colorScheme="green"
            >
              <Translate text="Necessary (Always enabled)" />
            </Checkbox>
            <Checkbox
              isChecked={cookiePreferences.analytics}
              onChange={() => handlePreferenceChange("analytics")}
              colorScheme="green"
            >
              <Translate text="Analytics Cookies" />
            </Checkbox>
            <Checkbox
              isChecked={cookiePreferences.marketing}
              onChange={() => handlePreferenceChange("marketing")}
              colorScheme="green"
            >
              <Translate text="Marketing Cookies" />
            </Checkbox>

            <Button
              color="white"
              bg="#286328"
              _hover="none"
              _active="none"
              size={{ base: "sm", md: "md" }}
              onClick={savePreferences}
              mt={4}
            >
              <Translate text="Save Preferences" />
            </Button>
          </VStack>
        )}
      </Box>
    </CookieConsent>
  );
};

const NewsletterSubscriptionPopup = ({ isOpen, onClose }) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Enter your email...");

  const [email, setEmail] = useState(""); // State to capture email input
  const [isTouched, setIsTouched] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const showEmailError = (isTouched || isSubmitted) && !validateEmail(email);

  const toast = useToast();

  const handleSubscribe = async () => {
    setIsSubmitted(true);

    if (!validateEmail(email)) {
      return; // Stop if email is invalid
    }

    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await axiosInstance.post("/newsletter/", { 
        email, 
        is_manual: true,
        date: new Date().toISOString()
      });
      setIsSubmitted(false);
      if (response.status === 201) {
        toast({
          title: "Subscribed!",
          description: "You've successfully subscribed to the newsletter.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setEmail(""); // Clear the email input after successful submission
        onClose(); // Close the modal after successful subscription
      }
    } catch (error) {
      setIsSubmitted(false);
      toast({
        title: "Error",
        description:
          error.response.data.message || "An error occurred. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInRight"
      size="md"
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent
        bg="#292929"
        color="white"
        m="0"
        mt={{ base: "auto", md: "10vh" }}
        bottom={{ base: "0", md: "auto" }}
        right={{ base: "0", md: "4" }}
        position="fixed"
        maxW={{ base: "100%", md: "400px", lg: "400px", xl: "500px" }}
      >
        <ModalCloseButton />
        <ModalBody>
          <HStack alignItems="center" spacing={4} mt={8} mb={4}>
            {/* Avatar video */}
            <Box
              as="video"
              src={AvatarVideo}
              borderRadius="full"
              autoPlay
              loop
              muted
              width={{ base: "40px", md: "80px" }}
              height={{ base: "40px", md: "80px" }}
              objectFit="cover"
            />

            <Heading
              color="#E1E6DB"
              as="h4"
              size={{ base: "sm", md: "md" }}
              mb={0}
            >
              <Translate text="Join the Green & Wild Community!" />
            </Heading>
          </HStack>
          <Text
            mb={4}
            color="#E1E6DB"
            textAlign="left"
            fontSize={{ base: "sm", md: "md" }}
          >
            <Translate
              text="Subscribe to NOMAD360' newsletter for personalized travel tips, epic
            outdoor experiences, and exclusive offers."
            />
          </Text>
          <FormControl isInvalid={showEmailError}>
            <Input
              placeholder={placeholderText}
              mb={4}
              borderColor="gray.600"
              size={{ base: "sm", md: "md" }}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (!isTouched) setIsTouched(true);
              }}
              onBlur={() => setIsTouched(false)}
              sx={{
                "::placeholder": {
                  color: "#E1E6DB", // or "blackAlpha.700" for a softer look
                  fontWeight: "medium",
                },
              }}
            />
            {showEmailError && (
              <FormErrorMessage>
                <Translate text="Please enter a valid email address." />
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            bg="#286328"
            color="#E1E6DB"
            _hover="none"
            mr={3}
            onClick={handleSubscribe}
            size={{ base: "sm", md: "md" }}
          >
            <Translate text="Subscribe" />
          </Button>
          <Button
            variant="outline"
            bg="#292929"
            color="#E1E6DB"
            _hover="none"
            onClick={onClose}
            size={{ base: "sm", md: "md" }}
          >
            <Translate text="Close" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const SurveyPopup = ({ isOpen, onClose }) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Write your feedback here...");
  const [surveyData, setSurveyData] = useState(null); // State to store survey data
  const [responses, setResponses] = useState([]); // State to store all the answers
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Track current question index
  const [validationError, setValidationError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false); // Track drag over state
  const toast = useToast();

  // Fetch the survey data when the modal is opened
  useEffect(() => {
    if (isOpen) {
      const fetchSurveyData = async () => {
        try {
          const response = await axiosInstance.get("/survey/fetch"); // Fetch the survey
          if (response.status === 200 && response.data.survey) {
            const survey = response.data.survey;
            setSurveyData(survey); // Set the entire survey data
          } else {
            toast({
              title: "No survey found.",
              description: "Please try again later.",
              status: "info",
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error fetching survey data: ", error);
          toast({
            title: "No survey found.",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
      };
      fetchSurveyData();
    }
  }, [isOpen, toast]);

  // Handle clipboard paste events for image questions
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));
      
      if (imageItems.length > 0) {
        // Get current question data safely
        const survey = surveyData;
        if (survey?.questions && survey.questions[currentQuestionIndex]?.type === 'image') {
          e.preventDefault();
          
          const files = imageItems.map(item => {
            const file = item.getAsFile();
            return file;
          }).filter(Boolean);
          
          if (files.length > 0) {
            const currentQuestionLabel = survey.questions[currentQuestionIndex].label;
            const currentFiles = responses.find(
              (res) => res.question === currentQuestionLabel
            )?.answer || [];
            handleResponseChange([...currentFiles, ...files], currentQuestionLabel, "image");
            toast({
              title: "Images Added",
              description: `${files.length} image${files.length > 1 ? 's' : ''} added via clipboard!`,
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, surveyData, currentQuestionIndex, responses]);

  // Clear validation error when modal closes
  useEffect(() => {
    if (!isOpen) {
      setValidationError("");
    }
  }, [isOpen]);

  // Handle the changes in responses (radio, text, textarea, image questions)
  const handleResponseChange = (value, questionLabel, questionType) => {
    setResponses((prevResponses) => {
      const existingResponseIndex = prevResponses.findIndex(
        (response) => response.question === questionLabel
      );

      if (existingResponseIndex !== -1) {
        // Update the existing response
        const updatedResponses = [...prevResponses];
        updatedResponses[existingResponseIndex] = {
          question: questionLabel,
          answer: value,
          type: questionType,
        };
        return updatedResponses;
      }

      // Add a new response
      return [
        ...prevResponses,
        { question: questionLabel, answer: value, type: questionType },
      ];
    });

    // Clear validation error if answering the current question
    const currentQuestion = surveyData?.questions?.[currentQuestionIndex];
    if (questionLabel === currentQuestion?.label) {
      setValidationError("");
    }
  };

  // Handle image file selection
  const handleImageChange = (e, questionLabel) => {
    const files = Array.from(e.target.files);
    handleResponseChange(files, questionLabel, "image");
    if (files.length > 0) {
      toast({
        title: "Images Added",
        description: `${files.length} image${files.length > 1 ? 's' : ''} added successfully!`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Check if a question has been answered (treat all questions as required)
  const isQuestionAnswered = (question) => {
    if (!question) return false;
    const matchingResponse = responses.find(
      (res) => res.question === question.label
    );
    if (!matchingResponse) return false;

    const answer = matchingResponse.answer;
    if (question.type === "image") {
      return Array.isArray(answer) && answer.length > 0;
    }
    if (typeof answer === "string") {
      return answer.trim().length > 0;
    }
    return Boolean(answer);
  };

  // Handle the click for "Continue" and "Submit" buttons
  const handleNextQuestion = () => {
    // Enforce answering current question before moving next (inline error)
    const currentQuestion = surveyData?.questions?.[currentQuestionIndex];
    const requireAnswerProvided = isQuestionAnswered(currentQuestion);
    if (!requireAnswerProvided) {
      setValidationError("Please answer this question before continuing.");
      return;
    }

    setValidationError("");
    if (currentQuestionIndex < totalQuestions - 1) setCurrentQuestionIndex((prev) => prev + 1);
  };

  // Add this handler for Previous button
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
    // Clear any lingering validation error when navigating back
    setValidationError("");
  };

  const handleSubmit = async () => {
    // Validate all questions are answered before submitting
    const firstUnansweredIndex = (surveyData?.questions || []).findIndex(
      (q) => !isQuestionAnswered(q)
    );
    if (firstUnansweredIndex !== -1) {
      setCurrentQuestionIndex(firstUnansweredIndex);
      setValidationError("Please answer all questions before submitting.");
      return;
    }
    setValidationError("");
    try {
      const payload = {
        heading: surveyData?.heading || "Survey", // Include the survey heading
        responses,
      };
      // Check if any response is of type image
      const hasImage = responses.some((r) => r.type === "image" && r.answer && r.answer.length > 0);
      let dataToSend = payload;
      let config = {};
      if (hasImage) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("heading", payload.heading);
        // Prepare a shallow copy of responses for serialization (replace File objects with placeholders)
        const responsesForJson = responses.map((resp, idx) => {
          if (resp.type === "image" && Array.isArray(resp.answer) && resp.answer.length > 0) {
            // We'll attach the files separately as image_i
            return { ...resp, answer: null };
          }
          return resp;
        });
        formData.append("responses", JSON.stringify(responsesForJson));
        // Attach each image file as image_i (i = index of the image question in the survey)
        responses.forEach((resp, idx) => {
          if (resp.type === "image" && Array.isArray(resp.answer)) {
            resp.answer.forEach((file, fileIdx) => {
              // If multiple images per question, use image_{idx}_{fileIdx}
              formData.append(`image_${idx}${resp.answer.length > 1 ? `_${fileIdx}` : ""}`, file);
            });
          }
        });
        dataToSend = formData;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }
      const response = await axiosInstance.post("/survey/submit", dataToSend, config);
      if (response.status === 201) {
        toast({
          title: "Success",
          description: response.data?.message || "Survey submitted successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose(); // Close the modal after successful submission
        setResponses([]);
      }
    } catch (error) {
      console.error("Error submitting survey: ", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to submit the survey.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!surveyData) return null;

  // Correctly calculate the total number of questions
  const totalQuestions = surveyData?.questions?.length || 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const currentQuestion = surveyData.questions
    ? surveyData.questions[currentQuestionIndex]
    : null;
  const progressPercentage =
    totalQuestions > 0
      ? ((currentQuestionIndex + 1) / totalQuestions) * 100
      : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInLeft"
      size="md"
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent
        bg="gray.800"
        color="white"
        // Remove ml, mt, left, and position to ensure centering
        maxW={{ base: "90%", md: "50%" }}
        maxH="78vh"
        minH="auto"
      >
        <ModalCloseButton />
        <ModalBody>
          <Heading color="white" as="h4" size="md" pl={2} mt={2} mb={4}>
            <Translate text={surveyData?.heading || "Survey"} />
          </Heading>
          <Box
            pl={2}
            pr={2}
            maxH="59vh"
            minH="auto"
            overflowY="auto"
            sx={{
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Text mb={4} fontSize="sm">
              <Translate text={surveyData?.subtext} />
            </Text>

            {/* Only show progress bar if there is more than one question */}
            {totalQuestions > 1 && (
              <>
                <Box
                  position="relative"
                  mb={4}
                  height="30px"
                  textAlign="center"
                >
                  {/* Progress bar with rounded corners and gradient */}
                  <Progress
                    value={progressPercentage}
                    size="lg"
                    borderRadius="full"
                    hasStripe={false}
                    isAnimated={false}
                    colorScheme="green"
                    bg="gray.200"
                    sx={{
                      "& > div": {
                        background:
                          "linear-gradient(90deg, #286328 0%, #6ecf80 100%)",
                      },
                    }}
                  />
                  {/* Overlay percentage text */}
                  <Text
                    position="absolute"
                    top="30%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    color="white"
                    fontWeight="bold"
                    mb={0}
                  >
                    {Math.round(progressPercentage)}%
                  </Text>
                </Box>
              </>
            )}

            {/* Display current question */}
            <Box mb={4}>
                          <Text fontSize="sm" mb={2} color="#D98D41">
              <Translate text={surveyData?.questions?.[currentQuestionIndex]?.label || ""} />
            </Text>

              {(() => {
                const currentQuestion = surveyData?.questions?.[currentQuestionIndex];
                return currentQuestion?.type === "radio" ? (
                  <RadioGroup
                    onChange={(value) =>
                      handleResponseChange(value, currentQuestion.label, "radio")
                    }
                    value={
                      responses.find(
                        (res) => res.question === currentQuestion.label
                      )?.answer || ""
                    }
                  >
                    <Stack direction="column">
                      {currentQuestion?.options?.map((option, optIndex) => (
                        <Radio key={optIndex} value={option}>
                          <Translate text={option} />
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                ) : null;
              })()}

              {(() => {
                const currentQuestion = surveyData?.questions?.[currentQuestionIndex];
                return currentQuestion?.type === "text" ? (
                  <Input
                    placeholder={placeholderText}
                    value={
                      responses.find(
                        (res) => res.question === currentQuestion.label
                      )?.answer || ""
                    }
                    onChange={(e) =>
                      handleResponseChange(
                        e.target.value,
                        currentQuestion.label,
                        "text"
                      )
                    }
                    size="sm"
                    borderColor="gray.600"
                  />
                ) : null;
              })()}

              {(() => {
                const currentQuestion = surveyData?.questions?.[currentQuestionIndex];
                return currentQuestion?.type === "image" ? (
                  <Box>
                    {/* Drag and Drop Zone */}
                    <Box
                      border="2px dashed"
                      borderColor={isDragOver ? "green.400" : "gray.400"}
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                      cursor="pointer"
                      transition="all 0.2s"
                      bg={isDragOver ? "green.50" : "transparent"}
                      _hover={{
                        borderColor: "green.400",
                        bg: "gray.700",
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(true);
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(false);
                        const files = Array.from(e.dataTransfer.files).filter(file => 
                          file.type.startsWith('image/')
                        );
                        if (files.length > 0) {
                          const currentQuestionLabel = surveyData?.questions?.[currentQuestionIndex]?.label;
                          if (currentQuestionLabel) {
                            const currentFiles = responses.find(
                              (res) => res.question === currentQuestionLabel
                            )?.answer || [];
                            handleResponseChange([...currentFiles, ...files], currentQuestionLabel, "image");
                            toast({
                              title: "Images Added",
                              description: `${files.length} image${files.length > 1 ? 's' : ''} added via drag & drop!`,
                              status: "success",
                              duration: 2000,
                              isClosable: true,
                            });
                          }
                        }
                      }}
                      onClick={() => document.getElementById(`file-input-${currentQuestionIndex}`).click()}
                    >
                      <VStack spacing={3}>
                        <Box
                          as="svg"
                          width="40px"
                          height="40px"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          color="gray.400"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </Box>
                        <Text fontSize="sm" color="gray.400">
                          <Translate text="Drag and drop image here, or click to browse" />
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          <Translate text="You can also paste images from clipboard (Ctrl+V)" />
                        </Text>
                      </VStack>
                    </Box>

                    {/* Hidden file input */}
                    <Input
                      id={`file-input-${currentQuestionIndex}`}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageChange(e, currentQuestion.label)}
                      display="none"
                    />

                    {/* Image previews */}
                    {(() => {
                      const currentQuestionLabel = surveyData?.questions?.[currentQuestionIndex]?.label;
                      const currentResponse = responses.find(
                        (res) => res.question === currentQuestionLabel
                      );
                      const hasImages = currentResponse?.answer && currentResponse.answer.length > 0;
                      
                      return hasImages ? (
                        <Box mt={4}>
                          <Text fontSize="sm" color="gray.400" mb={2}>
                            <Translate text="Selected Images:" />
                          </Text>
                          <Stack direction="row" spacing={2} flexWrap="wrap">
                            {currentResponse.answer.map((file, idx) => (
                              <Box 
                                key={idx} 
                                position="relative"
                                boxSize="80px" 
                                borderRadius="md" 
                                overflow="hidden" 
                                border="1px solid #ccc"
                              >
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`preview-${idx}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                                <Button
                                  position="absolute"
                                  top="2px"
                                  right="2px"
                                  size="xs"
                                  colorScheme="red"
                                  borderRadius="full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const updatedFiles = currentResponse.answer.filter((_, fileIdx) => fileIdx !== idx);
                                    handleResponseChange(updatedFiles, currentQuestionLabel, "image");
                                  }}
                                >
                                  ×
                                </Button>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                ) : null;
              })()}

              {/* Inline validation error for current step */}
              {validationError && (
                <Text mt={3} color="red.300" fontSize="sm">
                  <Translate text={validationError} />
                </Text>
              )}
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter justifyContent="space-between">
          {/* Previous button, only show if not on first question */}
          {totalQuestions > 1 && currentQuestionIndex > 0 && (
            <Button
              bg="#286328"
              color="white"
              _hover="none"
              ml={2}
              size="sm"
              onClick={handlePrevQuestion}
            >
              <Translate text="Previous" />
            </Button>
          )}
          <Spacer/>
          {/* Show "Continue" only if there is more than one question and not last question */}
          {totalQuestions > 1 && !isLastQuestion ? (
            <Button
              bg="#286328"
              color="white"
              _hover="none"
              mr={2}
              size="sm"
              onClick={handleNextQuestion}
            >
              <Translate text="Continue" />
            </Button>
          ) : (
            <Button
              bg="#286328"
              color="white"
              _hover="none"
              mr={3}
              size="sm"
              onClick={handleSubmit}
            >
              <Translate text="Submit" />
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { CookieConsentPopup, NewsletterSubscriptionPopup, SurveyPopup };
